import React, { useEffect, useState } from 'react';
import { Button, Header, Input, Modal, Table, Pagination } from 'react-frontier';
import { EventDateList, SetLoading } from '../SiteClasses';
import { timeConverter } from '../SiteUtil';
import moment from 'moment';
import API from '../API';

interface SearchCoordinatorEventModalProps{
	open: boolean,
	onClose: ()=>void,
	onSelected: (event: EventDateList)=>void,
	coordinatorId: number,
}

const PAGE_SIZE = 10;

var SearchCoordinatorEventModal = (props: SearchCoordinatorEventModalProps)=>{
	var [searching, setSearching] = useState<boolean>(false);
	var [searchInput, setSearchInput] = useState<string>('');
	var [events, setEvents] = useState<EventDateList[]>(null);
	var [searchResults, setSearchResults] = useState<EventDateList[]>(null);
	var [error, setError] = useState<string>(null);
	var [page, setPage] = useState<number>(0);
	var [isSearchMode, setIsSearchMode] = useState<boolean>(false);

	useEffect(()=>{
		if(props.open && props.coordinatorId){
			setSearchInput('');
			setSearchResults(null);
			setIsSearchMode(false);
			setPage(0);
			get(0);
		}
	}, [props.open, props.coordinatorId]);

	var get = (currentPage: number)=>{
		setSearching(true);
		setError(null);

		var dateStart = moment().subtract(1, 'week').startOf('day').toDate();
		var dateEnd = moment().add(1, 'week').endOf('day').toDate();

		API.getEvents(PAGE_SIZE, currentPage * PAGE_SIZE, dateStart, dateEnd, props.coordinatorId).then(res=>{
			if(res.error) return setError(res.message);
			setEvents(res.data);
		}).catch(()=>{
			setError('Hubo un error cargando los eventos del coordinador.');
		}).finally(()=>{
			setSearching(false);
		});
	};

	var searchEvents = (setLoading: SetLoading)=>{
		if(searching) return;
		setSearching(true);
		setError(null);
		setPage(0);

		if(searchInput.trim()){
			setIsSearchMode(true);
			if(setLoading) setLoading(true);
			API.searchEvents(searchInput, 0, PAGE_SIZE).then(res=>{
				if(res.error) return setError(res.message);
				var filtered = res.data?.filter(e=>e.user_coordinator === props.coordinatorId) || [];
				setSearchResults(filtered);
			}).catch(()=>{
				setError('Hubo un error buscando eventos.');
			}).finally(()=>{
				setSearching(false);
				if(setLoading) setLoading(false);
			});
		}else{
			setIsSearchMode(false);
			setSearchResults(null);
			setSearching(false);
			get(0);
		}
	};

	var onPageChange = (newPage: number)=>{
		setPage(newPage);
		if(isSearchMode && searchInput.trim()){
			setSearching(true);
			API.searchEvents(searchInput, newPage * PAGE_SIZE, PAGE_SIZE).then(res=>{
				if(res.error) return setError(res.message);
				var filtered = res.data?.filter(e=>e.user_coordinator === props.coordinatorId) || [];
				setSearchResults(filtered);
			}).catch(()=>{
				setError('Hubo un error buscando eventos.');
			}).finally(()=>{
				setSearching(false);
			});
		}else{
			get(newPage);
		}
	};

	var selectEvent = (event: EventDateList)=>{
		props.onSelected(event);
	};

	var shownResults = isSearchMode ? searchResults : events;
	var hasPagination = (shownResults?.length || 0) >= PAGE_SIZE || page !== 0;

	return <Modal open={props.open} onClose={props.onClose} size='tiny'>
		<Modal.Header>Buscar evento del coordinador</Modal.Header>
		<Modal.Content style={{ paddingBottom: hasPagination ? 0 : 20 }}>
			{props.open && (
				<Input
					value={searchInput}
					onChange={setSearchInput}
					placeholder='Buscar eventos...'
					inputType='text'
					button={<Button text='Buscar' onClick={searchEvents} loading={searching} />}
					onSubmit={searchEvents}
					submitOnEnter
				/>
			)}
			{searching ? (
				<Header loading text='Cargando eventos...' containerStyle={{ marginBottom: 50, marginTop: 50 }} />
			) : error ? (
				<Header text='Error' subtext={error} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
			) : !shownResults || shownResults.length === 0 ? (
				<Header text='Sin resultados' subtext='No se encontraron eventos del coordinador en el rango de fechas.' iconName='face-frown-open' style={{ marginTop: 50, marginBottom: 30 }} size='small' />
			) : (
				<Table
					fitted selectable striped divided
					style={{ marginTop: 20 }}
					className='first bottom round'
					headers={['ID', 'Evento', 'Fecha', 'Ciudad']}
					collapsingIndexes={[0, 2, 3]}
					footer={hasPagination && (
						<Table.Cell row colSpan={100}>
							<div style={{ justifyContent: 'flex-end', display: 'flex', marginBottom: 15 }}>
								<Pagination page={page} disabled={searching} onPageChange={onPageChange} hasNext={shownResults.length >= PAGE_SIZE} />
							</div>
						</Table.Cell>
					)}
				>
					{shownResults.map((e, i)=>(
						<Table.Row key={`COORD-EVT-${i}`} onClick={()=>selectEvent(e)}>
							<Table.Cell collapsing>{e.date_id}</Table.Cell>
							<Table.Cell>{e.event_name}</Table.Cell>
							<Table.Cell collapsing>{timeConverter(e.date)}</Table.Cell>
							<Table.Cell collapsing>{e.city || '-'}</Table.Cell>
						</Table.Row>
					))}
				</Table>
			)}
		</Modal.Content>
		<Modal.Actions>
			<Button text='Cerrar' basic onClick={props.onClose} />
		</Modal.Actions>
	</Modal>;
};

export default SearchCoordinatorEventModal;
